import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Upload, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SecondaryButton, StandardButton, DeleteButton } from '@/styles/StandardButtons';
import { pdfAPIService, type ParsedPdf } from '../services/pdf-api-service';
import { compareBidDocuments } from '../services/bid-ai-api';
import { bidComparisonService } from '../services/bid-comparison-service';
import { BidComparisonResults } from './bid-comparison-results';
import type { AiBidComparison } from '../types';

const MIN_BIDS = 2;
const MAX_BIDS = 6;

/**
 * AI Comparison: 2+ bids (PDFs dropped here, or bids selected in the Overview) -> one structured comparison.
 * Nothing the model said is shown raw: the server validates and normalises it, and the results component renders it.
 */
export function BidComparison({ preselectedBidIds = [] as string[] }: { preselectedBidIds?: string[] }) {
  const [documents, setDocuments] = useState<ParsedPdf[]>([]);
  const [result, setResult] = useState<AiBidComparison | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runId = useRef(0);

  // Bids selected in the Overview: compare the saved bids right away -- once per selection. (The key guard also keeps a
  // double-run of this effect from starting two AI operations: the second would be refused by the one-at-a-time limit.)
  const autoRunKey = useRef('');
  useEffect(() => {
    if (preselectedBidIds.length < MIN_BIDS) return;
    const key = preselectedBidIds.join(',');
    if (autoRunKey.current === key) return;
    autoRunKey.current = key;
    const id = ++runId.current;
    setIsComparing(true);
    setError(null);
    bidComparisonService.compareSavedBids(preselectedBidIds)
      .then((comparison) => { if (id === runId.current) setResult(comparison); })
      .catch((err) => { if (id === runId.current) setError(err instanceof Error ? err.message : 'The comparison failed. Please try again.'); })
      .finally(() => { if (id === runId.current) setIsComparing(false); });
  }, [preselectedBidIds]);

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setIsReading(true);
    setError(null);
    const failures: string[] = [];
    const read: ParsedPdf[] = [];
    for (const file of files) {
      try {
        read.push(await pdfAPIService.parsePDF(file));
      } catch (err) {
        failures.push(`${file.name}: ${err instanceof Error ? err.message : 'could not be read'}`);
      }
    }
    setDocuments((prev) => [...prev, ...read].slice(0, MAX_BIDS));
    if (read.length > 0) setResult(null);
    if (failures.length > 0) setError(failures.join('\n'));
    setIsReading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    noClick: true,
    disabled: isReading || isComparing,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const compare = async () => {
    const id = ++runId.current;
    setIsComparing(true);
    setError(null);
    try {
      const comparison = await compareBidDocuments(documents.map((doc) => ({ nameHint: doc.fileName, text: doc.text })));
      if (id === runId.current) setResult(comparison);
    } catch (err) {
      if (id === runId.current) setError(err instanceof Error ? err.message : 'The comparison failed. Please try again.');
    } finally {
      if (id === runId.current) setIsComparing(false);
    }
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div
            {...getRootProps()}
            className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
          >
            <input {...getInputProps()} ref={fileInputRef} />
            <Upload className="h-10 w-10 mx-auto mb-3 text-gray-400" />
            <p className="text-lg font-medium text-gray-700">Add 2 or more PDF bids here and AI will compare them for you.</p>
            <p className="text-sm text-gray-500 mt-1">Drag &amp; drop them, or pick them from your computer.</p>
            <div className="mt-4">
              <StandardButton type="button" customVariant="white" onClick={() => fileInputRef.current?.click()} disabled={isReading || isComparing || documents.length >= MAX_BIDS}>
                <Upload className="h-4 w-4 mr-2" />
                {isReading ? 'Reading PDFs...' : 'Upload PDF'}
              </StandardButton>
            </div>
          </div>

          {documents.length > 0 && (
            <div className="space-y-2">
              {documents.map((doc, index) => (
                <div key={`${doc.fileName}-${index}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                    <span className="font-medium truncate">{doc.fileName}</span>
                  </div>
                  <DeleteButton onClick={() => removeDocument(index)} aria-label={`Remove ${doc.fileName}`} disabled={isComparing} />
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <SecondaryButton onClick={compare} disabled={documents.length < MIN_BIDS || isComparing || isReading}>
                  {isComparing ? 'Comparing with AI...' : 'Compare Bids'}
                </SecondaryButton>
                {documents.length < MIN_BIDS && <span className="text-sm text-gray-500">Add {MIN_BIDS - documents.length} more PDF bid{MIN_BIDS - documents.length > 1 ? 's' : ''} to compare.</span>}
              </div>
            </div>
          )}

          {isComparing && preselectedBidIds.length >= MIN_BIDS && documents.length === 0 && (
            <p className="text-sm text-gray-600">Comparing your {preselectedBidIds.length} selected bids with AI...</p>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-start justify-between gap-3">
                <span className="whitespace-pre-line">{error}</span>
                <button type="button" aria-label="Dismiss" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {result && <BidComparisonResults result={result} />}
    </div>
  );
}
